/**
 * Оптимизированный рендеринг боя
 * Использует точечные обновления вместо полной перерисовки
 */

import { TYPE_NAMES } from '../constants.js';
import { escapeHtml } from '../utils.js';
import { getPanelByTeam } from './panels.js';
import { createBattleRenderOptimizer } from './renderOptimizer.js';
import { resolveSummonDisplay } from './summonUtils.js';
import { createGroupedStatusBadgesHtml } from './effectPresets.js';
import { getCurrentTurnAndAction } from './timeline.js';

// Создаем глобальный оптимизатор
const optimizer = createBattleRenderOptimizer();

// Кэш последних значений для сравнения
const lastPanelStates = new Map();
const lastLogState = { step: -1, html: '' };

/**
 * Оптимизированный рендеринг панелей бойцов
 * Обновляет только измененные элементы
 */
export function renderBattlePlaybackPanels(state) {
  const container = optimizer.getCachedElement('#battle-playback-panels');
  if (!container) return;
  
  const timeline = state.timeline;
  const step = timeline.length ? Math.min(Math.max(state.currentStep, 0), timeline.length - 1) : 0;
  const snapshot = timeline[step]?.snapshot || [];
  const snapshotMap = new Map(snapshot.map(item => [item.id, item]));
  const allPanels = state.panels.slice().sort((a, b) => a.team.localeCompare(b.team));
  
  // Этап 3: Определяем текущий ход и действие для динамического отображения
  const { turn, action } = getCurrentTurnAndAction(timeline, step, allPanels);
  
  // Показываем все панели (обе команды)
  // Ранее была фильтрация, но по требованию пользователя показываем все карточки
  let panels = allPanels;
  
  // Создаем карту существующих панелей
  const existingPanels = new Map();
  container.querySelectorAll('[data-panel-id]').forEach(el => {
    const panelId = el.getAttribute('data-panel-id');
    if (panelId) {
      existingPanels.set(panelId, el);
    }
  });
  
    // Скрываем панели, которые не должны быть видны
    existingPanels.forEach((element, panelId) => {
      const shouldBeVisible = panels.some(p => String(p.id) === panelId);
      if (!shouldBeVisible) {
        element.style.display = 'none';
      } else {
        element.style.display = '';
      }
    });
  
  // Обновляем или создаем панели
  panels.forEach(panel => {
    const panelId = String(panel.id);
    const panelSelector = `[data-panel-id="${panelId}"]`;
    let panelElement = existingPanels.get(panelId);
    
    // Если панели нет, создаем её
    const isNewPanel = !panelElement;
    if (isNewPanel) {
      panelElement = document.createElement('div');
      panelElement.className = `battle-combatant-panel battle-combatant-panel--team-${panel.team.toLowerCase()}`;
      panelElement.setAttribute('data-panel-id', panelId);
      container.appendChild(panelElement);
      existingPanels.set(panelId, panelElement);
      
      // При создании новой панели нужно сразу заполнить её полным HTML
      // Используем полный HTML из render.js для первоначального рендеринга
      const avatarHtml = panel.avatar
        ? `<img src="${escapeHtml(panel.avatar)}" alt="" onerror="this.onerror=null;this.src='/creatures/1.png';">`
        : `<div class="battle-combatant-avatar">${escapeHtml((panel.name || '').charAt(0).toUpperCase())}</div>`;
      const typeTags = panel.types && panel.types.length
        ? `<div class="battle-ability-tags">${panel.types
            .map(type => `<span class="battle-ability-tag">${escapeHtml(TYPE_NAMES[type] || type)}</span>`)
            .join('')}</div>`
        : '';
      const statusList = (state.statusTimeline?.[step]?.statuses?.[String(panel.id)]) || [];
      const statusHtml = statusList.length
        ? `<div class="battle-status-badges">${createGroupedStatusBadgesHtml(statusList, {
            showTooltip: true,
            showStack: true,
            showDuration: false,
            currentRound: state.currentRound || null
          })}</div>`
        : '';
      const lowHpClass = percent <= 30 ? 'battle-combatant-panel--low-hp' : '';
      const criticalHpClass = percent <= 15 ? 'battle-combatant-panel--critical-hp' : '';
      const hpColor = percent > 60 
        ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
        : percent > 30 
        ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
        : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)';
      
      panelElement.innerHTML = `
        <div class="battle-combatant-header">
          ${avatarHtml}
          <div class="battle-combatant-meta" style="flex: 1;">
            <div class="battle-combatant-name" style="font-size: 16px; font-weight: 700; color: var(--tg-theme-text-color); margin-bottom: 2px;">${escapeHtml(panel.name || '')}</div>
            <div class="battle-combatant-subtitle" style="font-size: 12px; color: var(--tg-theme-hint-color);">${escapeHtml(panel.subtitle || '')}</div>
          </div>
        </div>
        ${typeTags}
        ${statusHtml}
        <div class="battle-hp-track">
          <div class="battle-hp-row">
            <span>❤️ HP</span>
            <div class="battle-hp-bar" style="background: rgba(255,255,255,0.1); border-radius: 4px; height: 8px; width: 100%; position: relative; overflow: hidden;">
              <div class="battle-hp-fill" style="width:${percent}%; background: ${hpColor}; height: 100%; transition: width 0.5s ease, background 0.3s ease; border-radius: 4px;"></div>
            </div>
            <span style="font-weight:600; font-size: 0.9em; min-width: 50px; text-align: right;">${hpInfo.hp}/${panel.maxHp}</span>
          </div>
          <div class="battle-energy-row" style="margin-top: 6px;">
            <span>⚡ Энергия</span>
            <div class="battle-energy-bar" style="background: rgba(255,255,255,0.1); border-radius: 4px; height: 8px; width: 100%; position: relative; overflow: hidden;">
              <div class="battle-energy-fill" style="background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%); height: 100%; width: ${energyPercent}%; transition: width 0.5s ease; border-radius: 4px;"></div>
            </div>
            <span style="font-weight:600; font-size: 0.9em; min-width: 50px; text-align: right;">${energyInfo}/${maxEnergyInfo}</span>
          </div>
        </div>
      `;
      
      // Добавляем классы для HP
      if (lowHpClass) panelElement.classList.add(lowHpClass);
      if (criticalHpClass) panelElement.classList.add(criticalHpClass);
    }
    
    // Этап 3: Добавляем классы для атакующих/защищающихся карт
    if (action) {
      if (String(panel.id) === action.attacker) {
        panelElement.classList.add('battle-card-attacking');
        panelElement.classList.remove('battle-card-defending');
      } else if (String(panel.id) === action.defender) {
        panelElement.classList.add('battle-card-defending');
        panelElement.classList.remove('battle-card-attacking');
      } else {
        panelElement.classList.remove('battle-card-attacking', 'battle-card-defending');
      }
    } else {
      panelElement.classList.remove('battle-card-attacking', 'battle-card-defending');
    }
    
    // Получаем последнее состояние панели
    const lastState = lastPanelStates.get(panelId) || {};
    const hpInfo = snapshotMap.get(panel.id) || { hp: panel.maxHp, maxHp: panel.maxHp, energy: 100, maxEnergy: 100 };
    const percent = Math.max(0, Math.min(100, Math.round((hpInfo.hp / panel.maxHp) * 100)));
    const energyInfo = hpInfo.energy != null ? hpInfo.energy : (panel.raw?.state?.current?.energy ?? 100);
    const maxEnergyInfo = hpInfo.maxEnergy != null ? hpInfo.maxEnergy : (panel.raw?.state?.current?.maxEnergy ?? panel.raw?.state?.base?.maxEnergy ?? 100);
    const energyPercent = Math.max(0, Math.min(100, Math.round((energyInfo / maxEnergyInfo) * 100)));
    
    // Обновляем HP только если изменилось
    if (lastState.hp !== hpInfo.hp || lastState.maxHp !== panel.maxHp) {
      const hpFill = panelElement.querySelector('.battle-hp-fill');
      const hpText = panelElement.querySelector('.battle-hp-row span:last-child');
      const hpBar = panelElement.querySelector('.battle-hp-bar');
      
      if (hpFill) {
        // Динамический цвет HP
        const hpColor = percent > 60 
          ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
          : percent > 30 
          ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
          : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)';
        
        optimizer.updateStyle(`${panelSelector} .battle-hp-fill`, 'width', `${percent}%`);
        optimizer.updateStyle(`${panelSelector} .battle-hp-fill`, 'background', hpColor);
        
        // Добавляем класс для низкого HP
        if (percent <= 30) {
          panelElement.classList.add('battle-combatant-panel--low-hp');
        } else {
          panelElement.classList.remove('battle-combatant-panel--low-hp');
        }
        if (percent <= 15) {
          panelElement.classList.add('battle-combatant-panel--critical-hp');
        } else {
          panelElement.classList.remove('battle-combatant-panel--critical-hp');
        }
      }
      if (hpText) {
        optimizer.updateText(`${panelSelector} .battle-hp-row span:last-child`, `${hpInfo.hp}/${panel.maxHp}`);
      }
    }
    
    // Обновляем энергию только если изменилось
    if (lastState.energy !== energyInfo || lastState.maxEnergy !== maxEnergyInfo) {
      const energyRow = panelElement.querySelector('.battle-energy-row');
      const energyFill = panelElement.querySelector('.battle-energy-fill');
      const energyText = panelElement.querySelector('.battle-energy-row span:last-child');
      
      // Создаем элемент энергии, если его нет
      if (!energyRow) {
        const hpTrack = panelElement.querySelector('.battle-hp-track');
        if (hpTrack) {
          const energyRowDiv = document.createElement('div');
          energyRowDiv.className = 'battle-energy-row';
          energyRowDiv.style.marginTop = '4px';
          energyRowDiv.innerHTML = `
            <span>⚡ Энергия</span>
            <div class="battle-energy-bar" style="background: rgba(255,255,255,0.1); border-radius: 4px; height: 6px; width: 100%; position: relative; overflow: hidden;">
              <div class="battle-energy-fill" style="background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%); height: 100%; width: ${energyPercent}%; transition: width 0.3s ease;"></div>
            </div>
            <span style="font-weight:600; font-size: 0.9em;">${energyInfo}/${maxEnergyInfo}</span>
          `;
          hpTrack.appendChild(energyRowDiv);
        }
      } else {
        if (energyFill) {
          optimizer.updateStyle(`${panelSelector} .battle-energy-fill`, 'width', `${energyPercent}%`);
        }
        if (energyText) {
          optimizer.updateText(`${panelSelector} .battle-energy-row span:last-child`, `${energyInfo}/${maxEnergyInfo}`);
        }
      }
    }
    
    // Сохраняем текущее состояние
    lastPanelStates.set(panelId, {
      hp: hpInfo.hp,
      maxHp: panel.maxHp,
      energy: energyInfo,
      maxEnergy: maxEnergyInfo
    });
    
    // Обновляем имя только если изменилось
    if (lastState.name !== panel.name) {
      optimizer.updateText(`${panelSelector} .battle-combatant-name`, panel.name);
    }
    
    // Обновляем подзаголовок только если изменилось
    if (lastState.subtitle !== (panel.subtitle || '')) {
      const subtitleEl = panelElement.querySelector('.battle-combatant-subtitle');
      if (subtitleEl) {
        optimizer.updateText(`${panelSelector} .battle-combatant-subtitle`, panel.subtitle || '');
      }
    }
    
    // Обновляем статусы только если изменились
    const statusList = (state.statusTimeline?.[step]?.statuses?.[String(panel.id)]) || [];
    const statusKey = JSON.stringify(statusList.map(s => ({ icon: s.icon, label: s.label })));
    if (lastState.statusKey !== statusKey) {
      const statusContainer = panelElement.querySelector('.battle-status-badges');
      if (statusContainer) {
        if (statusList.length > 0) {
          // Используем улучшенную систему пресетов (Stage 4)
          const statusHtml = createGroupedStatusBadgesHtml(statusList, {
            showTooltip: true,
            showStack: true,
            showDuration: false,
            currentRound: state.currentRound || null
          });
          statusContainer.innerHTML = statusHtml;
        } else {
          statusContainer.innerHTML = '';
        }
      } else if (statusList.length > 0) {
        // Создаем контейнер статусов, если его нет
        // Используем улучшенную систему пресетов (Stage 4)
        const statusHtml = createGroupedStatusBadgesHtml(statusList, {
          showTooltip: true,
          showStack: true,
          showDuration: false,
          currentRound: state.currentRound || null
        });
        const statusDiv = document.createElement('div');
        statusDiv.className = 'battle-status-badges';
        statusDiv.innerHTML = statusHtml;
        panelElement.appendChild(statusDiv);
      }
    }
    
    // Обновляем типы только если изменились
    const typesKey = JSON.stringify(panel.types || []);
    if (lastState.typesKey !== typesKey) {
      const typesContainer = panelElement.querySelector('.battle-ability-tags');
      if (panel.types && panel.types.length > 0) {
        const typesHtml = panel.types
          .map(type => `<span class="battle-ability-tag">${escapeHtml(TYPE_NAMES[type] || type)}</span>`)
          .join('');
        if (typesContainer) {
          typesContainer.innerHTML = typesHtml;
        } else {
          const typesDiv = document.createElement('div');
          typesDiv.className = 'battle-ability-tags';
          typesDiv.innerHTML = typesHtml;
          panelElement.insertBefore(typesDiv, panelElement.querySelector('.battle-hp-track'));
        }
      } else if (typesContainer) {
        typesContainer.remove();
      }
    }
    
    // Сохраняем текущее состояние
    lastPanelStates.set(panelId, {
      hp: hpInfo.hp,
      maxHp: panel.maxHp,
      energy: energyInfo,
      maxEnergy: maxEnergyInfo,
      name: panel.name,
      subtitle: panel.subtitle || '',
      statusKey,
      typesKey
    });
  });
  
  // Этап 3: Скрываем/показываем панели в зависимости от видимости
  // Удаляем только те панели, которые действительно удалены из боя
  const allPanelIds = new Set((state.panels || []).map(p => String(p.id)));
  existingPanels.forEach((element, panelId) => {
    const isInBattle = allPanelIds.has(panelId);
    const isVisible = panels.some(p => String(p.id) === panelId);
    
    if (!isInBattle) {
      // Панель действительно удалена из боя (существо погибло)
      element.remove();
      lastPanelStates.delete(panelId);
      optimizer.clearCacheFor(`[data-panel-id="${panelId}"]`);
    } else if (!isVisible) {
      // Панель скрыта из-за фильтрации (динамическое отображение)
      element.style.display = 'none';
    } else {
      // Панель видима
      element.style.display = '';
    }
  });
}

/**
 * Оптимизированный рендеринг лога боя
 * Обновляет только если изменился шаг
 */
export function renderBattlePlaybackLog(state) {
  const logDiv = optimizer.getCachedElement('#battle-playback-log');
  if (!logDiv) return;
  
  const timeline = state.timeline;
  const step = Math.min(Math.max(state.currentStep, 0), timeline.length - 1);
  
  // Если шаг не изменился, не обновляем
  if (lastLogState.step === step && lastLogState.html) {
    return;
  }
  
  const current = timeline[step];
  let html = current ? current.description : '—';
  const statusSnapshot = state.statusTimeline?.[step]?.statuses || null;
  const round = current?.entry?.round ?? state.statusTimeline?.[step]?.round ?? null;
  
  if (statusSnapshot) {
    const rows = (state.panels || [])
      .map(panel => {
        const key = String(panel.id);
        const list = statusSnapshot[key];
        if (!list || !list.length) {
          return '';
        }
        // Используем улучшенную систему пресетов (Stage 4)
        const badges = createGroupedStatusBadgesHtml(list, {
          showTooltip: true,
          showStack: true,
          showDuration: false,
          currentRound: round || null
        });
        const name = escapeHtml(panel.name || `#${panel.id}`);
        return `<div class="battle-status-row"><div class="battle-status-row-label">${name}</div><div class="battle-status-row-badges">${badges}</div></div>`;
      })
      .filter(Boolean)
      .join('');
    if (rows) {
      html += `<div class="battle-status-summary">${rows}</div>`;
    }
  }
  
  if (round != null) {
    const telemetryMessages = state.telemetryByRound?.[round];
    if (Array.isArray(telemetryMessages) && telemetryMessages.length) {
      const eventsHtml = telemetryMessages
        .map(message => `<div class="battle-log-event">${escapeHtml(message)}</div>`)
        .join('');
      html += `<div class="battle-log-effects">${eventsHtml}</div>`;
    }
  }
  
  // Обновляем только если HTML изменился
  if (lastLogState.html !== html) {
    logDiv.innerHTML = html;
    lastLogState.html = html;
  }
  lastLogState.step = step;
}

/**
 * Оптимизированный рендеринг сцены боя
 * Обновляет только измененные элементы
 */
export function renderBattlePlaybackStage(state) {
  const stage = optimizer.getCachedElement('#battle-stage-center');
  if (!stage) return;
  
  const timeline = state.timeline;
  const step = timeline.length ? Math.min(Math.max(state.currentStep, 0), timeline.length - 1) : 0;
  const current = timeline[step];
  const entry = current?.entry;
  
  // Обновляем только если изменился шаг или данные
  const stageKey = `${step}:${entry?.round || 0}:${entry?.event || ''}`;
  const lastStageKey = stage.dataset?.stageKey || '';
  
  if (lastStageKey === stageKey) {
    return; // Не изменилось, пропускаем
  }
  
  // Обновляем сцену
  const arena = state.battle?.arena || 'THE_STACK';
  const round = entry?.round || state.round || 0;
  const newText = `${arena} • Раунд ${round}`;
  
  if (stage.textContent !== newText) {
    stage.textContent = newText;
  }
  stage.dataset.stageKey = stageKey;
}

/**
 * Очистка кэша при сбросе боя
 */
export function clearBattleRenderCache() {
  optimizer.clearCache();
  lastPanelStates.clear();
  lastLogState.step = -1;
  lastLogState.html = '';
}

